import {
  TextInput as AdminTextInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanField,
  BulkDeleteWithConfirmButton,
  CloneButton,
  CreateButton,
  DatagridConfigurable,
  DateField,
  DateInput,
  DeleteWithConfirmButton,
  EditButton,
  ExportButton,
  FilterButton,
  FilterForm,
  FunctionField,
  NullableBooleanInput,
  List as RAList,
  ReferenceInput,
  ReferenceManyField,
  SelectColumnsButton,
  SelectInput,
  TextField,
  TopToolbar,
  UserIdentity,
  downloadCSV,
  useDataProvider,
  useGetIdentity,
  useListContext,
  useListFilterContext,
  useRecordContext,
  useRefresh,
  useResourceContext,
} from "react-admin";
import { Box, Button, Card, Tooltip } from "@mui/material";
import { DeliveryNote, Order, OrderStatus } from "src/@generated/schemas";
import { sum, sumBy } from "lodash";

import { CheckBox } from "@mui/icons-material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CustomPagination from "src/CustomPagination";
import DownloadIcon from "@mui/icons-material/GetApp";
import { GlobalListStyles } from "src/libs/globalListStyles";
import cookie from "js-cookie";
import dayjs from "dayjs";
import { exportToExcel } from "src/libs/exportToExcel";
import i18next from "src/locales";
import jsonExport from "jsonexport/dist";
import { url } from "../../";
import { useState } from "react";
import { useTranslate } from "src/locales";

const FORMAT_DATE = "DD.MM.YYYY HH:mm";

const convertOrderStatusToString = (orderStatus: OrderStatus) => {
  if (orderStatus === OrderStatus.Blocked) {
    return i18next.translate("resources.Order.status.blocked");
  }
  if (orderStatus === OrderStatus.Cancelled) {
    return i18next.translate("resources.Order.status.cancelled");
  }
  if (orderStatus === OrderStatus.Avisation) {
    return i18next.translate("resources.Order.status.avisation");
  }
  if (orderStatus === OrderStatus.Confirmed) {
    return i18next.translate("resources.Order.status.confirmed");
  }
  if (orderStatus === OrderStatus.Declined) {
    return i18next.translate("resources.Order.status.declined");
  }
  if (orderStatus === OrderStatus.InProgress) {
    return i18next.translate("resources.Order.status.inProgress");
  }
  if (orderStatus === OrderStatus.Planned) {
    return i18next.translate("resources.Order.status.planned");
  }
  if (orderStatus === OrderStatus.Invoiced) {
    return i18next.translate("resources.Order.status.invoiced");
  }
  if (orderStatus === OrderStatus.Unknown) {
    return i18next.translate("resources.Order.status.unknown");
  }
};

export const OrdersFilter = (identity: UserIdentity | undefined) => [
  <AdminTextInput key="id" source="orderNumber" clearAlwaysVisible />,
  <AdminTextInput
    key="referenceOrderNumber"
    source="referenceOrderNumber"
    clearAlwaysVisible
  />,
  <DateInput key="loadingDate_gte" source="loadingDate_gte" alwaysOn />,
  <DateInput key="loadingDate_lte" source="loadingDate_lte" alwaysOn />,
  <AdminTextInput key="loadingName" source="loadingName" clearAlwaysVisible />,
  <AdminTextInput key="loadingCity" source="loadingCity" clearAlwaysVisible />,
  <AdminTextInput key="loadingCountry" source="loadingCountry" clearAlwaysVisible />,
  <DateInput key="unloadingDate_gte" source="unloadingDate_gte" alwaysOn />,
  <DateInput key="unloadingDate_lte" source="unloadingDate_lte" alwaysOn />,
  <AdminTextInput
    key="unloadingName"
    source="unloadingName"
    clearAlwaysVisible
  />,
  <AdminTextInput
    key="unloadingCity"
    source="unloadingCity"
    clearAlwaysVisible
  />,
  <AdminTextInput
    key="unloadingCountry"
    source="unloadingCountry"
    clearAlwaysVisible
  />,
  <AdminTextInput key="invoiceNumber" source="invoiceNumber" />,
  <AdminTextInput key="driver" source="driver" />,
  <AdminTextInput key="licensePlate" source="licensePlate" />,
  <ReferenceInput
    reference="User"
    source="user_id"
    filter={{
      selectedCompanyId: {
        equals: identity?.selectedCompany?.id,
      },
    }}
  >
    <AutocompleteInput optionText="email" label="Email" />
  </ReferenceInput>,
  <SelectInput
    key="isMain"
    source="isMain"
    emptyText="Vše"
    defaultValue={null}
    choices={[
      {
        id: true,
        name: i18next.translate("resources.Order.fields.justMain"),
      },
      {
        id: false,
        name: i18next.translate("resources.Order.fields.withoutMain"),
      },
    ]}
  />,
  <ReferenceInput reference="DeliveryNote" source="deliveryNotes_ids">
    <AutocompleteInput optionText="deliveryNoteNumber" label="Dodací list" />
  </ReferenceInput>,
  <NullableBooleanInput
    source="caveats"
    label="Výhrady"
    trueLabel={i18next.translate("resources.Order.fields.withCaveats")}
    falseLabel={i18next.translate("resources.Order.fields.withoutCaveats")}
    nullLabel={i18next.translate("resources.Global.all")}
  />,
  <SelectInput
    key="status"
    alwaysOn
    source="status"
    emptyText={i18next.translate("resources.Global.all")}
    choices={[
      {
        id: OrderStatus.InProgress,
        name: convertOrderStatusToString(OrderStatus.InProgress),
      },
      {
        id: OrderStatus.Confirmed,
        name: convertOrderStatusToString(OrderStatus.Confirmed),
      },
      {
        id: OrderStatus.Invoiced,
        name: convertOrderStatusToString(OrderStatus.Invoiced),
      },
      {
        id: OrderStatus.Planned,
        name: convertOrderStatusToString(OrderStatus.Planned),
      },
      {
        id: OrderStatus.Avisation,
        name: convertOrderStatusToString(OrderStatus.Avisation),
      },
    ]}
  />,
];

const getSelectedColumnsForExport = (): { source: string; label: string }[] => {
  const columnsRaw = localStorage.getItem(
    "RaStore.preferences.Order.datagrid.columns",
  );
  const availableRaw = localStorage.getItem(
    "RaStore.preferences.Order.datagrid.availableColumns",
  );

  if (!columnsRaw || !availableRaw) {
    return DEFAULT_EXPORT_COLUMNS;
  }

  try {
    const selectedIndexes: string[] = JSON.parse(columnsRaw);
    const availableColumns: {
      index: string;
      source?: string;
      label: string;
    }[] = JSON.parse(availableRaw);

    return selectedIndexes
      .map((index) => availableColumns.find((col) => col.index === index))
      .filter((col) => col?.source) as { source: string; label: string }[];
  } catch (err) {
    console.warn("Chyba při čtení vybraných sloupců:", err);
    return DEFAULT_EXPORT_COLUMNS;
  }
};

const DEFAULT_EXPORT_COLUMNS: { source: string; label: string }[] = [
  { source: "referenceOrderNumber", label: "Ref. obj." },
  { source: "orderNumber", label: "Číslo objednávky" },
  { source: "orderType", label: "Typ zboží" },
  { source: "profitCenter", label: "Středisko" },
  { source: "action", label: "Akce" },
  { source: "loadingDate", label: "Datum nakládky" },
  { source: "loadingPostcode", label: "PSČ nakládky" },
  { source: "loadingCity", label: "Místo nakládky" },
  { source: "loadingCountry", label: "Země nakládky" },
  { source: "loadingNote", label: "Poznámka nakládky" },
  { source: "unloadingDate", label: "Datum vykládky" },
  { source: "unloadingPostcode", label: "PSČ vykládky" },
  { source: "unloadingCity", label: "Místo vykládky" },
  { source: "unloadingCountry", label: "Země vykládky" },
  { source: "unloadingNote", label: "Poznámka vykládky" },
  { source: "mass", label: "Hmotnost" },
  { source: "numberOfItems", label: "Počet kusů" },
  { source: "cash", label: "Částka hotovost" },
  { source: "cost", label: "Cena včetně mýta" },
  { source: "currency", label: "Měna" },
  { source: "noteForInvoice", label: "Poznámka do faktury" },
  { source: "noteOnInvoice", label: "Poznámka pro fakturaci" },
  { source: "noteForDispatch", label: "Poznámka pro dispečery" },
  { source: "invoiceNumber", label: "Variabilní symbol" },
  { source: "deliveryNotes", label: "Dodací listy" },
  { source: "sscc", label: "Štítky" },
  { source: "user.email", label: "Založil" },
  { source: "status", label: "Stav" },
  { source: "caveats", label: "Výhrady" },
  { source: "caveatsNotes", label: "Výhrady poznámka" },
  { source: "createdAt", label: "Vytvořeno" },
  { source: "driver", label: "Řidič" },
  { source: "licensePlate", label: "SPZ" },
];

const getExportColumns = (items: Order[]) => {
  const selectedColumns = getSelectedColumnsForExport();

  const data = items.map((item) => {
    const row: Record<string, any> = {};

    selectedColumns.forEach(({ source, label: originalLabel }) => {
      let value: any = "";
      let label: any = originalLabel || "";

      let number = 0;

      for (const deliveryNote of item?.deliveryNotes || []) {
        number += deliveryNote.sscc.length;
      }

      switch (source) {
        case "orderNumber":
          value =
            item.orderNumber ||
            item.externalSystemOrderNumberIngres ||
            item.externalSystemOrderNumberCarlo;
          label = i18next.translate("resources.Order.fields.orderNumber");
          break;
        case "referenceOrderNumber":
          value = item.referenceOrderNumber;
          label = i18next.translate(
            "resources.Order.fields.referenceOrderNumber",
          );
          break;
        case "status":
          value = convertOrderStatusToString(item.status);
          label = i18next.translate("resources.Order.fields.status");
          break;
        case "loadingDate":
          value = dayjs(item.loadingDate).format(FORMAT_DATE);
          label = i18next.translate("resources.Order.fields.loadingDate");
          break;
        case "unloadingDate":
          value = dayjs(item.unloadingDate).format(FORMAT_DATE);
          label = i18next.translate("resources.Order.fields.unloadingDate");
          break;
        case "user.email":
          value = item.user?.email;
          label = i18next.translate("resources.Order.fields.user_id");
          break;
        case "sscc":
          value = number;
          label = i18next.translate("resources.Order.fields.ssccLabels");
          break;
        case "deliveryNotes":
          value = (item.deliveryNotes ?? [])
            .map((d) => d.deliveryNoteNumber)
            .join(", ");
          label = i18next.translate("resources.Order.fields.deliveryNotes_ids");
          break;
        case "cash":
          const cashByCurrency = (item.deliveryNotes ?? []).reduce(
            (acc: Record<string, number>, deliveryNote: DeliveryNote) => {
              if (deliveryNote.cash == null || !deliveryNote.currency) {
                return acc;
              }

              acc[deliveryNote.currency] =
                (acc[deliveryNote.currency] ?? 0) + deliveryNote.cash;

              return acc;
            },
            {},
          );

          value = Object.entries(cashByCurrency)
            .map(([currency, amount]) => `${amount},- ${currency}`)
            .join(" / ");

          label = i18next.translate("resources.Order.fields.cash");

          break;
        case "cost":
          value = item.cost || 0;
          label = i18next.translate("resources.Order.fields.cost");
          break;
        case "currency":
          value = item.currency || "";
          label = i18next.translate("resources.Order.fields.currency");
          break;
        case "createdAt":
          value = dayjs(item.createdAt).format(FORMAT_DATE);
          label = i18next.translate("resources.Order.fields.createdAt");
          break;
        case "orderType":
          value = item.orderType;
          label = i18next.translate("resources.Order.fields.orderType");
          break;
        case "profitCenter":
          value = item.profitCenter;
          label = i18next.translate("resources.Order.fields.profitCenter");
          break;
        case "action":
          value = item.action;
          label = i18next.translate("resources.Order.fields.action");
          break;
        case "loadingCity":
          value = item.loadingCity;
          label = i18next.translate("resources.Order.fields.loadingCity");
          break;
        case "loadingStreet":
          value = item.loadingStreet;
          label = i18next.translate("resources.Order.fields.loadingStreet");
          break;
        case "loadingName":
          value = item.loadingName;
          label = i18next.translate("resources.Order.fields.loadingName");
          break;
        case "loadingCountry":
          value = item.loadingCountry;
          label = i18next.translate("resources.Order.fields.loadingCountry");
          break;
        case "loadingNote":
          value = item.loadingNote;
          label = i18next.translate("resources.Order.fields.loadingNote");
          break;
        case "loadingPostcode":
          value = item.loadingPostcode;
          label = i18next.translate("resources.Order.fields.loadingPostcode");
          break;
        case "unloadingCity":
          value = item.unloadingCity;
          label = i18next.translate("resources.Order.fields.unloadingCity");
          break;
        case "unloadingName":
          value = item.unloadingName;
          label = i18next.translate("resources.Order.fields.unloadingName");
          break;
        case "unloadingStreet":
          value = item.unloadingStreet;
          label = i18next.translate("resources.Order.fields.unloadingStreet");
          break;
        case "unloadingCountry":
          value = item.unloadingCountry;
          label = i18next.translate("resources.Order.fields.unloadingCountry");
          break;
        case "unloadingNote":
          value = item.unloadingNote;
          label = i18next.translate("resources.Order.fields.unloadingNote");
          break;
        case "unloadingPostcode":
          value = item.unloadingPostcode;
          label = i18next.translate("resources.Order.fields.unloadingPostcode");
          break;
        case "mass":
          value = item.mass;
          label = i18next.translate("resources.Order.fields.mass");
          break;
        case "numberOfItems":
          value = item.numberOfItems;
          label = "Počet kusů";
          label = i18next.translate("resources.Order.fields.numberOfItems");
          break;
        case "noteForDispatch":
          value = item.noteForDispatch;
          label = i18next.translate("resources.Order.fields.noteForDispatch");
          break;
        case "noteForInvoice":
          value = item.noteForInvoice;
          label = "Poznámka do faktury";
          label = i18next.translate("resources.Order.fields.noteForInvoice");
          break;
        case "noteOnInvoice":
          value = item.noteOnInvoice;
          label = "Poznámka pro fakturu";
          label = i18next.translate("resources.Order.fields.noteOnInvoice");
          break;
        case "invoiceNumber":
          value = item.invoiceNumber;
          label = i18next.translate("resources.Order.fields.invoiceNumber");
          break;
        case "driver":
          value = item.driver;
          label = i18next.translate("resources.Order.fields.driver");
          break;
        case "licensePlate":
          value = item.licensePlate;
          label = i18next.translate("resources.Order.fields.licensePlace");
          break;
        case "Výhrady.id":
        case "Výhrady.createdAt":
        case "Výhrady.createdBy":
          break;
        case "caveats":
          value = item.caveats.map((caveat) => caveat.name).join("\n");
          label = i18next.translate("resources.Order.fields.caveats");
          break;
        case "caveatsNotes":
          value = item.caveats.map((caveat) => caveat.note).join("\n");
          label = i18next.translate("resources.Order.fields.caveatsNotes");
          break;
        default:
          // fallback na přímý přístup
          value = item[source as keyof Order];
      }

      if (!["Reporty"].includes(source)) {
        row[label] = value;
      }
    });

    return row;
  });

  return data;
};

const customExporterCSV = (items: Order[]) => {
  const listForExport = getExportColumns(items);

  jsonExport(listForExport, {}, (_, csv) => {
    downloadCSV(
      "\ufeff" + csv,
      `${i18next.translate("resources.Order.name")}.csv`,
    );
  });
};

const customExporterXLS = (items: Order[]) => {
  const listForExport = getExportColumns(items);

  exportToExcel(
    listForExport,
    `${i18next.translate("resources.Order.name")}.xlsx`,
  );
};

const rowStyle = (record: any) => ({
  padding: 4,
  backgroundColor:
    record?.status === OrderStatus.InProgress ? "#ffcd38" : undefined,
});

const fetchAll = async (dataProvider, resource, filter, sort) => {
  const perPage = 100000; // dávka
  let page = 1,
    all = [];
  for (; ;) {
    const { data, total } = await dataProvider.getList(resource, {
      filter,
      sort,
      pagination: { page, perPage },
    });
    all = all.concat(data);
    if (all.length >= total || data.length === 0) break;
    page += 1;
  }
  return all;
};

const Actions: React.FC = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { displayedFilters, setFilters, showFilter } = useListFilterContext();
  const { filterValues, sort, selectedIds } = useListContext();
  const dataProvider = useDataProvider();
  const resource = useResourceContext();

  const handleExportRosterClick = () => {
    const query = encodeURIComponent(
      JSON.stringify({
        ...filterValues,
        companyId: identity.selectedCompany.id,
      }),
    );

    window.open(`${url}/order/soupiska?where=${query}`, "_blank");
  };

  return (
    <Box width="100%">
      <TopToolbar>
        <FilterForm
          displayedFilters={displayedFilters}
          filterValues={filterValues}
          setFilters={setFilters}
          showFilter={showFilter}
          filters={OrdersFilter(identity)}
          style={{ marginRight: 30 }}
        />
        {!identity?.selectedCompany?.isBlocked && <CreateButton />}
        <FilterButton
          filters={OrdersFilter(identity)}
          style={{ marginLeft: 30, marginRight: 15 }}
        />
        <Button
          size="small"
          style={{ marginRight: 55, top: 3 }}
          onClick={handleExportRosterClick}
        >
          <DownloadIcon style={{ height: 18 }} />
          {translate("resources.Global.exportRoster")}
        </Button>
        <SelectColumnsButton style={{ marginRight: 40 }} />
        <ExportButton
          label={translate("resources.Global.exportCSV")}
          exporter={undefined}
          onClick={async () => {
            const records = await fetchAll(
              dataProvider,
              resource,
              filterValues,
              sort,
            );
            customExporterCSV(records || []);
          }}
          style={{ marginRight: 40 }}
        />
        <ExportButton
          label={translate("resources.Global.exportExcel")}
          exporter={undefined}
          onClick={async () => {
            const records = await fetchAll(
              dataProvider,
              resource,
              filterValues,
              sort,
            );
            customExporterXLS(records || []);
          }}
        />
      </TopToolbar>
    </Box>
  );
};

export const DownloadDeliveryNoteZip = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  const handleOnClick = () => {
    if (!record.orderNumber) {
      return null;
    }

    window
      .open(
        `${url}/order/sscc-template?ids=${record.id}&access_token=${cookie.get(
          "access_token",
        )}`,
        "_blank",
      )
      .focus();
  };

  return (
    <Tooltip
      title={
        record.orderNumber
          ? ""
          : translate("resources.Order.errors.importInProgress")
      }
    >
      <Button onClick={handleOnClick} startIcon={<CloudDownloadIcon />}>
        {translate("resources.Order.fields.ssccLabels")}
      </Button>
    </Tooltip>
  );
};

const BulkActionButtons = () => {
  const translate = useTranslate();
  const { selectedIds } = useListContext();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const handleExportRosterClick = () => {
    const query = encodeURIComponent(
      JSON.stringify({
        ids: selectedIds,
        companyId: identity.selectedCompany.id,
      }),
    );

    window.open(`${url}/order/soupiska?where=${query}`, "_blank");
  };

  return (
    <>
      <a
        href="#"
        onClick={handleExportRosterClick}
      >
        {translate("resources.Global.exportRoster")}
      </a>
      <a
        target="_blank"
        href={`${url}/order/download-multiple?ids=${selectedIds}&access_token=${cookie.get(
          "access_token",
        )}`}
        download
      >
        {translate("resources.Order.fields.downloadDeliveryNotes")}
      </a>
      <a
        target="_blank"
        href={`${url}/order/sscc-template?ids=${selectedIds}&access_token=${cookie.get(
          "access_token",
        )}`}
        download
      >
        {translate("resources.Order.fields.ssccLabels")}
      </a>
      <BulkDeleteWithConfirmButton mutationMode="pessimistic" mutationOptions={{
        onSuccess: () => {
          refresh();
        },
      }} />
    </>
  );
};

export const OrdersList = () => {
  const [downloaded, setDownloaded] = useState([]);
  const translate = useTranslate();
  const { identity } = useGetIdentity();

  return (
    <RAList
      actions={<Actions />}
      empty={false}
      pagination={<CustomPagination />}
      sort={{ field: "createdAt", order: "DESC" }}
      filterDefaultValues={{
        inProcess: false,
        isMain: "",
        loadingDate_gte: dayjs().format("YYYY-MM-DD"),
        loadingDate_lte: dayjs().add(1, "day").format("YYYY-MM-DD"),
      }}
      exporter={null}
      sx={{
        width: "calc(100vw - 50px)",
      }}
    >
      <GlobalListStyles />
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 180px)",
          overflow: "hidden",
          width: "calc(100vw - 50px)",
        }}
      >
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DatagridConfigurable
            size="small"
            bulkActionButtons={<BulkActionButtons />}
            rowStyle={rowStyle}
            sx={{
              minWidth: "900px",
              "& tr td": { paddingTop: 0, paddingBottom: 0 },
              "& .RaDatagrid-rowOdd": { backgroundColor: "#f0f0f0" },
              "& thead": {
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 2,
              },
            }}
          >
            <TextField source="referenceOrderNumber" />
            {identity?.selectedCompany?.carloPartnerId ? (
              <FunctionField
                source="externalSystemOrderNumberCarlo"
                label="resources.Order.fields.orderNumber"
                render={(row) =>
                  row.externalSystemOrderNumberIngres
                    ? `${row.orderNumber || "-"}${row.externalSystemOrderNumberCarlo
                      ? ` (${row.externalSystemOrderNumberCarlo})`
                      : ""
                    }`
                    : row.externalSystemOrderNumberCarlo || ""
                }
              />
            ) : (
              <TextField
                source="orderNumber"
                sortBy="externalSystemOrderNumberIngres"
              />
            )}
            <TextField source="orderType" />
            <TextField source="profitCenter" />
            <TextField source="action" />
            <DateField source="loadingDate" />
            <TextField source="loadingName" />
            <TextField source="loadingPostcode" />
            <TextField source="loadingCity" />
            <TextField source="loadingCountry" />
            <TextField source="loadingNote" />
            <DateField source="unloadingDate" />
            <TextField source="unloadingName" />
            <TextField source="unloadingPostcode" />
            <TextField source="unloadingCity" />
            <TextField source="unloadingCountry" />
            <TextField source="unloadingNote" />
            <FunctionField source="mass" render={(row) => `${row.mass}kg`} />
            <TextField source="numberOfItems" />
            <FunctionField
              source="cost"
              render={(row) => `${row.cost || 0} ${row.currency || ""}`}
            />
            <FunctionField
              source="cash"
              render={(row) => {
                const cashByCurrency = (row.deliveryNotes ?? []).reduce(
                  (acc: Record<string, number>, deliveryNote: DeliveryNote) => {
                    if (deliveryNote.cash == null || !deliveryNote.currency) {
                      return acc;
                    }

                    acc[deliveryNote.currency] =
                      (acc[deliveryNote.currency] ?? 0) + deliveryNote.cash;

                    return acc;
                  },
                  {},
                );

                return Object.entries(cashByCurrency)
                  .map(([currency, amount]) => `${amount},- ${currency}`)
                  .join(' / ');
              }}
            />
            <TextField source="note" />
            <TextField source="noteForInvoice" />
            <TextField source="noteForDispatch" />
            <TextField source="noteOnInvoice" />
            <TextField source="invoiceNumber" />
            <TextField source="driver" />
            <TextField source="licensePlate" />
            <ReferenceManyField
              sortBy="caveats._count"
              target="caveats_ids"
              label={translate("resources.Order.fields.caveats")}
              reference="Caveat"
            >
              <FunctionField
                source="caveats.name"
                label={translate("resources.Order.fields.caveats")}
                render={(row) =>
                  row.caveats.map((caveat) => (
                    <div key={caveat.id}>{caveat.name}</div>
                  ))
                }
              />
            </ReferenceManyField>
            <ReferenceManyField
              sortBy="caveats._count"
              target="caveats_ids"
              reference="Caveat"
              label={translate("resources.Order.fields.caveatsNotes")}
            >
              <FunctionField
                source="caveats.note"
                render={(row) =>
                  row.caveats.map((caveat) => (
                    <div key={caveat.id}>{caveat.note}</div>
                  ))
                }
              />
            </ReferenceManyField>
            <FunctionField
              source="deliveryNotes"
              sortable={false}
              label={translate("resources.Order.fields.deliveryNotes_ids")}
              render={(row: any) => {
                return (
                  <div key={row.id}>
                    {(row.deliveryNotes || []).map((deliveryNote) => (
                      <div key={deliveryNote.id}>
                        {deliveryNote.deliveryNoteNumber}
                        <br />
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            <FunctionField
              source="sscc"
              render={(row) => {
                let number = 0;

                for (const deliveryNote of row?.deliveryNotes || []) {
                  number += deliveryNote.sscc.length;
                }

                return <div>{number}</div>;
              }}
            />
            <TextField source="user.email" />
            <BooleanField source="isMain" />
            <FunctionField
              source="status"
              render={(row) => convertOrderStatusToString(row.status)}
            />
            <FunctionField
              source="reports"
              render={(row) => {
                let number = 0;

                for (const deliveryNote of row?.deliveryNotes || []) {
                  number += deliveryNote.sscc.length;
                }

                if (!row?.confirmedAt || !number) {
                  return null;
                }

                return (
                  <a
                    target="_blank"
                    href={`${url}/order/sscc-template?ids=${row.id
                      }&access_token=${cookie.get("access_token")}`}
                    download
                    onClick={() => setDownloaded((prev) => [...prev, row.id])}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color:
                        row?.downloadedSsccAt || downloaded.includes(row.id)
                          ? "#2e7d32"
                          : "blue",
                    }}
                  >
                    {translate("resources.Order.fields.ssccLabels")}{" "}
                    {row?.downloadedSsccAt || downloaded.includes(row.id) ? (
                      <CheckBox
                        style={{ marginLeft: 4, width: 20 }}
                        color="success"
                      />
                    ) : (
                      <CloudDownloadIcon style={{ marginLeft: 4, width: 20 }} />
                    )}
                  </a>
                );
              }}
            />
            <FunctionField
              source="createdAt"
              render={(row) => (
                <div style={{ whiteSpace: "nowrap" }}>
                  {dayjs(row.createdAt).format("DD.MM YYYY HH:mm")}
                </div>
              )}
            />
            <EditButton />
            <CloneButton />
            <DeleteWithConfirmButton />
          </DatagridConfigurable>
        </Box>
      </Card>
    </RAList>
  );
};
